import type {
  Histo,
  HistoTableRow,
  HistoMetrics,
  HistoLearningsCost,
} from "@/components/Admin/Histo/HistoDataTable/types";

export function useHistoTable() {
  function getAdsMargin(metrics: HistoMetrics | null): number | null {
    if (!metrics) return null;
    return Math.round((1 - metrics.cost / metrics.revenue_capped) * 100);
  }

  function getRecruitmentMargin(metrics: HistoMetrics | null, learningsCosts: number): number | null {
    if (!metrics) return null;
    return Math.round((1 - (metrics.cost + learningsCosts) / metrics.revenue_capped) * 100);
  }

  function getCPI(metrics: HistoMetrics | null): number | null {
    if (!metrics) return null;
    return Math.round((metrics.cost / metrics.completes_valid_capped) * 10) / 10;
  }

  function getClientCPI(metrics: HistoMetrics | null): number | null {
    if (!metrics) return null;
    return Math.round((metrics.revenue_capped / metrics.completes_valid_capped) * 10) / 10;
  }

  function getCompletionRate(metrics: HistoMetrics | null): number | null {
    if (!metrics) return null;
    const denominator = metrics.started - (metrics.screenouts + metrics.no_targets + metrics.quota_full);
    if (denominator <= 0) return null;
    return Math.round((metrics.completes_valid / denominator) * 100);
  }

  function getScreenoutRate(metrics: HistoMetrics | null): number | null {
    if (!metrics) return null;
    const denominator = metrics.started - (metrics.dropouts + metrics.no_targets + metrics.quota_full);
    if (denominator <= 0) return null;
    return Math.round((metrics.screenouts / denominator) * 100);
  }

  function getLOI(metrics: HistoMetrics | null): number | null {
    if (!metrics || !metrics.loi_respondents) return null;
    return Math.round(metrics.sum_loi / metrics.loi_respondents);
  }

  function getCPM(metrics: HistoMetrics | null): number | null {
    if (!metrics || !metrics.impressions) return null;
    return Math.round((metrics.cost / metrics.impressions) * 10000) / 10;
  }

  function getCTR(metrics: HistoMetrics | null): number | null {
    if (!metrics || !metrics.impressions) return null;
    return Math.round((metrics.clicks / metrics.impressions) * 10000) / 100;
  }

  function getCPC(metrics: HistoMetrics | null): number | null {
    if (!metrics || !metrics.clicks) return null;
    return Math.round((metrics.cost / metrics.clicks) * 100) / 100;
  }

  function getStartRate(metrics: HistoMetrics | null): number | null {
    if (!metrics || !metrics.sc) return null;
    return Math.round((metrics.started / metrics.sc) * 100);
  }

  function getCPS(metrics: HistoMetrics | null): number | null {
    if (!metrics || !metrics.started) return null;
    return Math.round((metrics.cost / metrics.started) * 100) / 100;
  }

  function getRevenue(metrics: HistoMetrics | null): number | null {
    if (!metrics) return null;
    return Math.round(metrics.revenue_capped);
  }

  function calculateLearningsCosts(histo: Histo): number {
    try {
      const learningsCosts: HistoLearningsCost[] = JSON.parse(histo.learnings_costs);
      return learningsCosts
        .filter((el) => ["Incentives", "Panel"].includes(el.cost_type))
        .reduce((acc, item) => acc + item.cost, 0);
    } catch {
      return 0;
    }
  }

  function getSimilarity(histo: Histo): number | null {
    if (histo.similarity_global == null) return null;
    return Math.round(histo.similarity_global * 100);
  }

  function transformHistoToRow(
    histo: Histo,
    countryCode: string,
    metrics: HistoMetrics | null,
    isSubRow: boolean = false,
    parentId?: string,
  ): HistoTableRow {
    const countryMetrics = JSON.parse(histo.country_metrics || "{}");
    const isMultiCountry = Object.keys(countryMetrics).length > 1;
    const learningsCosts = calculateLearningsCosts(histo);

    return {
      id: isSubRow ? `${histo.survey_name}-${countryCode}` : histo.survey_name,
      surveyName: histo.survey_name,
      studyType: histo.study_type,
      countryCode,
      isMultiCountry: isMultiCountry && !isSubRow,
      isSubRow,
      parentId,
      surveyType: histo.survey_type,
      accountName: histo.account_name,
      targetDescription: histo.hs_target_description,
      completedAt: histo.completed_at,
      completesValidCapped: metrics?.completes_valid_capped ?? null,
      revenue: getRevenue(metrics),
      adsMargin: getAdsMargin(metrics),
      recruitmentMargin: getRecruitmentMargin(metrics, learningsCosts),
      cpi: getCPI(metrics),
      clientCpi: getClientCPI(metrics),
      completionRate: getCompletionRate(metrics),
      screenoutRate: getScreenoutRate(metrics),
      loi: getLOI(metrics),
      days: metrics?.days ?? null,
      cpm: getCPM(metrics),
      ctr: getCTR(metrics),
      cpc: getCPC(metrics),
      startRate: getStartRate(metrics),
      cps: getCPS(metrics),
      similarity: getSimilarity(histo),
      originalHisto: histo,
    };
  }

  function transformHistosToTableRows(histos: Histo[]): HistoTableRow[] {
    return histos.map((histo) => {
      const countryMetrics = JSON.parse(histo.country_metrics || "{}");
      const countries = Object.keys(countryMetrics);
      const isMultiCountry = countries.length > 1;

      let mainMetrics: HistoMetrics | null = null;
      let mainCountryCode = "global";

      if (isMultiCountry) {
        mainMetrics = JSON.parse(histo.global_metrics || "null");
      } else if (countries.length === 1) {
        mainCountryCode = countries[0];
        mainMetrics = countryMetrics[mainCountryCode];
      }

      const mainRow = transformHistoToRow(histo, mainCountryCode, mainMetrics, false);

      if (isMultiCountry) {
        mainRow.subRows = countries.map((country) => {
          const countryData = countryMetrics[country];
          return transformHistoToRow(histo, country, countryData, true, mainRow.id);
        });
      }

      return mainRow;
    });
  }

  return {
    transformHistosToTableRows,
    getAdsMargin,
    getRecruitmentMargin,
    getCPI,
    getClientCPI,
    getCompletionRate,
    getScreenoutRate,
    getLOI,
    getCPM,
    getCTR,
    getCPC,
    getStartRate,
    getCPS,
    getRevenue,
  };
}
